import { configService } from '$lib/services/config';

import type { OfferProduct, PaymentMethods } from '@api/types';
import { ExperimentGroup, ExperimentType } from '@shared/experiment';
import { Billing, PaymentMethod } from '@shared/types';

import { getTotalPrice } from './functions';
import type { PaymentMethodWithAlias } from './types';

/**
 * In FF011 experiment group C, crypto payment method should be optimistically
 * added to the list of visible payment methods if some of the available
 * products total prices meets any crypto billing productPriceMin threshold.
 */
export function ff011_checkIfCryptoEligibleForSome(
    isTrial: boolean,
    paymentMethodName: PaymentMethod | undefined,
    availableProducts: OfferProduct[],
    cryptoBillings: PaymentMethods[0]['billings']): boolean {
    const group = configService.getExperimentGroup(ExperimentType.FF011);
    if (isTrial ||
        paymentMethodName !== PaymentMethod.Crypto ||
        group !== ExperimentGroup.C) {
        return false;
    }

    return availableProducts.some(product => isSomeCryptoBillingAvailableFor(product, cryptoBillings));
}

/**
 * If experiment FF011 group C, logic was applied, this utility should handle payment selection.
 * It tries to find all eligible products meeting the productPriceMin threshold of available
 * crypto billings. If currently selected product fits that condition, skipCallback is applied,
 * otherwise applyCallback would be called passing eligible product id with the minimal
 * total price.
 */
export function ff011_trySelectProductEligibleForCryptoPayment(
    isTrial: boolean,
    paymentMethod: PaymentMethodWithAlias,
    selectedProductId: OfferProduct['id'] | null,
    visibleProducts: OfferProduct[],
    visiblePaymentMethods: PaymentMethods,
    skipCallback: () => void,
    applyCallback: (productId: OfferProduct['id']) => void): void {
    const cryptoMethod = visiblePaymentMethods.find(pm => pm.paymentMethod === PaymentMethod.Crypto);
    if (!cryptoMethod ||
        !selectedProductId ||
        !ff011_checkIfCryptoEligibleForSome(isTrial, paymentMethod?.name, visibleProducts, cryptoMethod.billings)) {
        skipCallback();
        return;
    }

    // find all crypto payable products
    const cryptoPayable = visibleProducts
        .filter(product => isSomeCryptoBillingAvailableFor(product, cryptoMethod.billings));

    // in case currently selected product is in the list of payable options do nothing
    if (cryptoPayable.some(p => p.id === selectedProductId)) {
        skipCallback();
        return;
    }

    const cheapestCryptoPayableProduct = cryptoPayable.length === 0
        ? null
        : cryptoPayable.reduce((min, curr) =>
            getTotalPrice(curr.price, curr.period) < getTotalPrice(min.price, min.period) ? curr : min);

    if (cheapestCryptoPayableProduct) {
        applyCallback(cheapestCryptoPayableProduct.id);
    }
}

/**
 * In FF011 experiment group C, crypto payment method is optimistically available.
 * It is possible that user selects crypto payment provider and then selects a product
 * from available options which is not payable because of crypto billing ProductMinPrice threshold.
 * In that case, utility attempts to find any non crypto payment method which is not affected
 * by experiment. This should fix the situation when user tries to pay for unfeasible product with crypto.
 *
 * FFO11 group C should not affect Premium products (isTrial) and other methods except Crypto.
 *
 * @param isTrial - used to skip utility logic, it doesn't meet FF011/C
 * @param currentMethod - payment method which currently selected
 * @param selectedProductId - product which is currently selected
 * @param visibleProducts - list of products visible to the user
 * @param visiblePaymentMethods - list of payment methods visible to the user
 * @param applyCallback - callback to select fallback payment method
 */
export function ff011_tryUnselectCryptoIfProductTooCheap(
    isTrial: boolean,
    currentMethod: PaymentMethodWithAlias | null,
    selectedProductId: OfferProduct['id'] | null,
    visibleProducts: OfferProduct[],
    visiblePaymentMethods: PaymentMethods,
    applyCallback: (newMethod: PaymentMethodWithAlias) => void): void {
    const cryptoPayment = visiblePaymentMethods.find(pm => pm.paymentMethod === PaymentMethod.Crypto);
    if (!cryptoPayment ||
        !selectedProductId ||
        !ff011_checkIfCryptoEligibleForSome(isTrial, currentMethod?.name, visibleProducts, cryptoPayment?.billings)) {
        return;
    }

    const selectedProduct = visibleProducts.find(s => s.id === selectedProductId);
    const noCryptoBillingsForProduct = selectedProduct &&
        !isSomeCryptoBillingAvailableFor(selectedProduct, cryptoPayment.billings);
    const nonCryptoMethod  = visiblePaymentMethods.find(pm => pm !== cryptoPayment);

    if (noCryptoBillingsForProduct && nonCryptoMethod) {
        applyCallback({
            name: nonCryptoMethod.paymentMethod as PaymentMethod,
            alias: nonCryptoMethod.aliases?.[0],
            billing: nonCryptoMethod.billings[0].billing as Billing,
        });
    }
}

function isSomeCryptoBillingAvailableFor(
    product: OfferProduct,
    cryptoBillings: PaymentMethods[0]['billings']):boolean {
    const productTotalPrice = getTotalPrice(product.price, product.period);

    if (!product.pricePerMinute) {
        return false;
    }

    return cryptoBillings.some(b => {
        if (b.productPriceMin) {
            if (productTotalPrice < b.productPriceMin) {
                return false;
            }
        }

        return product.paymentSettings && !product.paymentSettings.some((s) => {
            return s.excludedPaymentMethods?.includes(PaymentMethod.Crypto) && s.billing === b.billing;
        });
    });
}
